
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { Message } from "element-ui";
import { IAddShippingModel, IUserModel } from "@/api/types";
import {
  getFactoryLoadingPlan,
  updateFactoryLoadingPlan,
} from "@/api/factory-loading-plans";
import { getUsersByJobPosition } from "@/api/users";
import { getShipping } from "@/api/shipping";
import { COMPANY_OPTIONS } from "@/constant";
import {
  highlightKeywords,
  matchesAllKeywords,
} from "@/utils/keyword-search";
import { timeParser } from "@/filters";

interface EditForm {
  declaration: boolean | null;
  combinedContainer: boolean | null;
  shippingOrderId: string | null;
}

@Component({
  name: "EditFactoryLoadingPlanDialog",
})
export default class EditFactoryLoadingPlanDialog extends Vue {
  @Prop({ required: true }) private visible!: boolean;
  @Prop({ default: null }) private planId!: string | null;

  private plan: any = null;
  private detailLoading = false;
  private listLoading = false;
  private submitting = false;
  private keyword = "";
  private shippingOrderList: IAddShippingModel[] = [];
  private sellerList: IUserModel[] = [];
  private logisticsClerkList: IUserModel[] = [];
  private usersLoaded = false;
  private currentShippingCode = "";

  private form: EditForm = {
    declaration: null,
    combinedContainer: null,
    shippingOrderId: null,
  };

  get planCode(): string {
    return (this.plan && this.plan.code) || "-";
  }

  get companyLabel(): string {
    const code = this.plan && this.plan.companyCode;
    if (!code) return "-";
    return COMPANY_OPTIONS[code] || code;
  }

  get customerAbbr(): string {
    if (!this.plan) return "";
    return this.plan.customerAbbreviation || this.plan.customerAbbr || "";
  }

  get customerCode(): string {
    return (this.plan && this.plan.customerCode) || "";
  }

  get estimatedDateLabel(): string {
    const raw = this.plan && this.plan.estimatedSailingSchedule;
    if (!raw) return "-";
    const formatted = timeParser(raw, "YYYY-MM-DD");
    return formatted || "-";
  }

  get logisticsClerkName(): string {
    if (this.plan && this.plan.logisticsClerkName) {
      return this.plan.logisticsClerkName;
    }
    const id = this.plan && this.plan.logisticsClerkId;
    if (!id) return "";
    const found = this.logisticsClerkList.find((u) => u.id === id);
    return found ? found.name : "";
  }

  get sellerName(): string {
    if (this.plan && this.plan.sellerName) {
      return this.plan.sellerName;
    }
    const id = this.plan && this.plan.sellerId;
    if (!id) return "";
    const found = this.sellerList.find((u) => u.id === id);
    return found ? found.name : "";
  }

  get searchKeywords(): string[] {
    return (this.keyword || "")
      .trim()
      .split(/\s+/)
      .filter(Boolean);
  }

  get filteredOrders(): IAddShippingModel[] {
    const keywords = this.searchKeywords.map((term) => term.toLowerCase());
    if (!keywords.length) return this.shippingOrderList;
    return this.shippingOrderList.filter((item) =>
      matchesAllKeywords([String(item.shippingCode || "")], keywords)
    );
  }

  get selectedOrder(): IAddShippingModel | null {
    if (!this.form.shippingOrderId) return null;
    return (
      this.shippingOrderList.find(
        (item) => item.id === this.form.shippingOrderId
      ) || null
    );
  }

  @Watch("visible")
  private onVisibleChange(val: boolean) {
    if (val) {
      this.bootstrap();
    }
  }

  private async bootstrap() {
    this.resetLocal();
    await Promise.all([this.ensureUsersLoaded(), this.loadDetail()]);
  }

  private resetLocal() {
    this.keyword = "";
    this.shippingOrderList = [];
    this.currentShippingCode = "";
    this.form = {
      declaration: null,
      combinedContainer: null,
      shippingOrderId: null,
    };
    this.plan = null;
  }

  private async ensureUsersLoaded() {
    if (this.usersLoaded) return;
    const [sellers, clerks]: any = await Promise.all([
      getUsersByJobPosition("seller"),
      getUsersByJobPosition("logistics-clerk"),
    ]);
    this.sellerList = sellers || [];
    this.logisticsClerkList = clerks || [];
    this.usersLoaded = true;
  }

  private async loadDetail() {
    if (!this.planId) {
      Message.error("缺少报关计划 ID");
      return;
    }
    this.detailLoading = true;
    try {
      const data: any = await getFactoryLoadingPlan(this.planId);
      this.plan = data;
      this.form = {
        declaration:
          data.declaration == null ? null : !!data.declaration,
        combinedContainer:
          data.combinedContainer == null ? null : !!data.combinedContainer,
        shippingOrderId: data.shippingOrderId || null,
      };
      this.currentShippingCode = data.shippingOrderCode || "";

      if (this.form.declaration === true && this.form.combinedContainer !== null) {
        await this.loadShippingOrders();
      }
    } finally {
      this.detailLoading = false;
    }
  }

  private onDeclarationChange(value: boolean) {
    this.form.declaration = value;
    if (!value) {
      this.form.combinedContainer = null;
      this.form.shippingOrderId = null;
      this.shippingOrderList = [];
      this.keyword = "";
      this.currentShippingCode = "";
    }
  }

  private async onCombinedChange(value: boolean) {
    this.form.combinedContainer = value;
    this.form.shippingOrderId = null;
    this.currentShippingCode = "";
    this.keyword = "";
    await this.loadShippingOrders();
  }

  private selectOrder(id: string) {
    this.form.shippingOrderId =
      this.form.shippingOrderId === id ? null : id;
    if (this.form.shippingOrderId) {
      const found = this.shippingOrderList.find(
        (item) => item.id === this.form.shippingOrderId
      );
      this.currentShippingCode = found ? found.shippingCode : "";
    } else {
      this.currentShippingCode = "";
    }
  }

  private highlightShippingCode(text: string): string {
    return highlightKeywords(text, this.searchKeywords);
  }

  private async loadShippingOrders() {
    if (
      !this.plan ||
      !this.plan.customerId ||
      this.form.combinedContainer === null
    ) {
      this.shippingOrderList = [];
      return;
    }
    this.listLoading = true;
    try {
      const data: any = await getShipping({
        combinedContainer: this.form.combinedContainer,
        customerId: this.plan.customerId,
        company: this.plan.companyCode,
      });
      const list: IAddShippingModel[] = Array.isArray(data) ? data.slice() : [];
      if (
        this.form.shippingOrderId &&
        this.currentShippingCode &&
        !list.some((item) => item.id === this.form.shippingOrderId)
      ) {
        list.unshift({
          id: this.form.shippingOrderId,
          shippingCode: this.currentShippingCode,
          abbreviation: this.customerAbbr,
        } as IAddShippingModel);
      }
      this.shippingOrderList = list;
    } finally {
      this.listLoading = false;
    }
  }

  private handleClose() {
    this.$emit("update:visible", false);
  }

  private async handleSubmit() {
    if (!this.plan || !this.plan.id) {
      Message.error("报关计划信息不完整");
      return;
    }
    if (this.form.declaration === null || this.form.declaration === undefined) {
      Message.error("请选择是否报关");
      return;
    }

    this.submitting = true;
    try {
      const payload = {
        ...this.plan,
        declaration: this.form.declaration,
        combinedContainer:
          this.form.declaration === true
            ? this.form.combinedContainer
            : null,
        shippingOrderId:
          this.form.declaration === true
            ? this.form.shippingOrderId
            : null,
      };

      await updateFactoryLoadingPlan(this.plan.id, payload);
      this.$notify({
        title: "成功",
        message: "编辑成功",
        type: "success",
        duration: 2000,
      });
      this.$emit("success");
      this.handleClose();
    } finally {
      this.submitting = false;
    }
  }
}
